window.addEventListener('load', function () {
  'use strict';

  /* =========================================================
     EPDM GROUPED LENGTH PICKER
     VAT-AWARE VERSION

     Shopify variant prices are EX VAT.
     Uses Meadway VAT preference for display only.
     ========================================================= */

  const VAT_STORAGE_KEY = 'meadway-vat-mode';
  const VAT_MULTIPLIER = 1.20;

  const root = document.querySelector(
    '[data-epdm-grouped-picker]'
  );

  if (!root) return;

  const variantJson = document.querySelector(
    '[data-epdm-length-variants]'
  );

  if (!variantJson) return;

  let variants = [];

  try {
    variants = JSON.parse(
      variantJson.textContent
    );
  } catch (e) {
    console.error(
      'EPDM grouped picker JSON parse error',
      e
    );

    return;
  }

  const groupsWrap = root.querySelector(
    '[data-epdm-length-groups]'
  );

  const decimalWrap = root.querySelector(
    '[data-epdm-decimal-wrap]'
  );

  const decimalSelect = root.querySelector(
    '[data-epdm-decimal-select]'
  );

  if (
    !groupsWrap ||
    !decimalWrap ||
    !decimalSelect
  ) {
    return;
  }

  /*
    Keep track of the selected exact-length variant.

    This is important when EX VAT / INC VAT is changed.
  */
  let currentVariantId = null;

  /* =========================================================
     HELPERS
     ========================================================= */

  function parseLength(title) {
    const cleaned = String(title)
      .replace('m', '')
      .trim();

    const num = Number(cleaned);

    if (!Number.isFinite(num)) {
      return null;
    }

    return num;
  }

  function getWholeGroup(num) {
    return Math.floor(num);
  }

  function getVatMode() {
    /*
      Prefer the main Meadway VAT system.
    */
    if (
      window.MeadwayVAT &&
      typeof window.MeadwayVAT.getMode === 'function'
    ) {
      return window.MeadwayVAT.getMode();
    }

    /*
      Fallback to localStorage.
    */
    try {
      return localStorage.getItem(
        VAT_STORAGE_KEY
      ) === 'inc'
        ? 'inc'
        : 'ex';
    } catch (error) {
      return 'ex';
    }
  }

  function getVatLabel() {
    if (
      window.MeadwayVAT &&
      typeof window.MeadwayVAT.getLabel === 'function'
    ) {
      return window.MeadwayVAT.getLabel();
    }

    return getVatMode() === 'inc'
      ? 'inc VAT'
      : 'ex VAT';
  }

  function formatMoneyFromPence(pence) {
    /*
      Shopify gives us the genuine EX VAT price in pence.
    */
    const exVatValue =
      Number(pence || 0) / 100;

    const vatMode =
      getVatMode();

    const displayValue =
      vatMode === 'inc'
        ? exVatValue * VAT_MULTIPLIER
        : exVatValue;

    return new Intl.NumberFormat(
      'en-GB',
      {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    ).format(displayValue);
  }

  /* =========================================================
     UPDATE VISIBLE PRODUCT PRICE
     ========================================================= */

  function updateVisiblePrice(variantId) {
    const selectedVariant = variants.find(
      (variant) =>
        String(variant.id) ===
        String(variantId)
    );

    if (!selectedVariant) {
      console.warn(
        'EPDM picker: variant not found:',
        variantId
      );

      return;
    }

    currentVariantId =
      String(selectedVariant.id);

    /*
      Always calculate from the RAW Shopify variant price.

      Never calculate from the price currently displayed
      on screen.
    */
    const formattedPrice =
      formatMoneyFromPence(
        selectedVariant.price
      );

    const vatLabel =
      getVatLabel();

    /*
      Keep the same selectors from the picker version that
      successfully updates your Shopify product price.
    */
    const possiblePriceSelectors = [
      '[data-product-price]',
      '.price',
      '.product__price',
      '.price__current',
      '.product-price',
      '.price-item--regular'
    ];

    possiblePriceSelectors.forEach(
      (selector) => {
        document
          .querySelectorAll(selector)
          .forEach((el) => {
            /*
              =================================================
              IMPORTANT FIX
              =================================================

              Previously we tested:

                el.children.length === 0

              But after the first update we added:

                <span class="mw-vat-suffix">

              so children.length became 1 and the price was
              never updated again.

              Now we IGNORE our own VAT suffix when deciding
              whether this is a simple writable price element.
            */

            const meaningfulChildren =
              Array.from(el.children).filter(
                (child) =>
                  !child.classList.contains(
                    'mw-vat-suffix'
                  )
              );

            const canUpdate =
              meaningfulChildren.length === 0 ||
              el.classList.contains(
                'price-item--regular'
              );

            if (!canUpdate) {
              return;
            }

            /*
              Remove old VAT suffix first.
            */
            el
              .querySelectorAll(
                '.mw-vat-suffix'
              )
              .forEach(
                (suffix) =>
                  suffix.remove()
              );

            /*
              Replace the previous price.

              textContent also clears any stale text from the
              previous variant.
            */
            el.textContent =
              formattedPrice;

            /*
              Add fresh VAT status.
            */
            const suffix =
              document.createElement(
                'span'
              );

            suffix.className =
              'mw-vat-suffix';

            suffix.textContent =
              vatLabel;

            el.appendChild(
              suffix
            );
          });
      }
    );
  }

  /* =========================================================
     GROUP VARIANTS
     ========================================================= */

  const grouped = {};

  variants.forEach((variant) => {
    const len =
      parseLength(
        variant.title
      );

    if (!Number.isFinite(len)) {
      return;
    }

    const whole =
      getWholeGroup(len);

    if (!grouped[whole]) {
      grouped[whole] = [];
    }

    grouped[whole].push({
      id: variant.id,
      title: variant.title,
      available:
        variant.available,
      price:
        variant.price,
      length: len
    });
  });

  /* =========================================================
     BUILD WHOLE-METRE BUTTONS
     ========================================================= */

  Object.keys(grouped)
    .sort(
      (a, b) =>
        Number(a) -
        Number(b)
    )
    .forEach((groupKey) => {
      const btn =
        document.createElement(
          'button'
        );

      btn.type =
        'button';

      btn.className =
        'epdm-length-pill';

      btn.textContent =
        groupKey;

      btn.dataset.group =
        groupKey;

      btn.addEventListener(
        'click',
        function () {
          document
            .querySelectorAll(
              '.epdm-length-pill'
            )
            .forEach((el) => {
              el.classList.remove(
                'is-active'
              );
            });

          btn.classList.add(
            'is-active'
          );

          populateDecimals(
            groupKey
          );
        }
      );

      groupsWrap.appendChild(
        btn
      );
    });

  /* =========================================================
     EXACT LENGTH OPTIONS
     ========================================================= */

  function populateDecimals(
    groupKey
  ) {
    decimalSelect.innerHTML =
      '<option value="">Select length</option>';

    const items =
      grouped[groupKey] || [];

    items.sort(
      (a, b) =>
        a.length -
        b.length
    );

    items.forEach((item) => {
      const opt =
        document.createElement(
          'option'
        );

      opt.value =
        String(item.id);

      opt.textContent =
        item.title;

      if (!item.available) {
        opt.disabled =
          true;
      }

      decimalSelect.appendChild(
        opt
      );
    });

    decimalWrap.hidden =
      false;
  }

  /* =========================================================
     SELECT REAL SHOPIFY VARIANT
     ========================================================= */

  function selectRealVariant(
    variantId
  ) {
    currentVariantId =
      String(variantId);

    /*
      Update Shopify's native variant control.
    */
    const targetRadio =
      document.querySelector(
        'variant-picker input[type="radio"][data-variant-id="' +
          variantId +
          '"]'
      );

    if (targetRadio) {
      targetRadio.checked =
        true;

      targetRadio.dispatchEvent(
        new Event(
          'change',
          {
            bubbles: true
          }
        )
      );
    }

    /*
      Update hidden variant ID used by:

      - Add to basket
      - Buy now
      - Shop Pay
    */
    const hiddenVariantInput =
      document.querySelector(
        'input[name="id"]'
      );

    if (hiddenVariantInput) {
      hiddenVariantInput.value =
        variantId;

      hiddenVariantInput.dispatchEvent(
        new Event(
          'change',
          {
            bubbles: true
          }
        )
      );
    }

    /*
      Update our visible EPDM price.
    */
    updateVisiblePrice(
      variantId
    );
  }

  /* =========================================================
     EXACT LENGTH CHANGE
     ========================================================= */

  decimalSelect.addEventListener(
    'change',
    function () {
      const variantId =
        decimalSelect.value;

      if (!variantId) {
        return;
      }

      selectRealVariant(
        variantId
      );
    }
  );

  /* =========================================================
     VAT TOGGLE CHANGE
     ========================================================= */

  /*
    Main Meadway VAT script dispatches this whenever EX VAT /
    INC VAT changes.

    We simply redraw the CURRENT selected variant using its
    original Shopify price.
  */
  window.addEventListener(
    'meadway:vat-mode-changed',
    function () {
      if (!currentVariantId) {
        return;
      }

      updateVisiblePrice(
        currentVariantId
      );
    }
  );

  /* =========================================================
     INITIALISE FIRST GROUP
     ========================================================= */

  const firstGroupButton =
    groupsWrap.querySelector(
      '.epdm-length-pill'
    );

  if (firstGroupButton) {
    firstGroupButton.click();
  }
});